import { useState } from "react";
import { motion } from "framer-motion";
import { Gem, Skull } from "lucide-react";
import { useSession, ModeNote, Panel, Label, Stat } from "./usePlay";
import { BetControl } from "@/components/game/BetControl";
import { fmtAZN } from "@/lib/api";
import { sfx } from "@/lib/sound";

const DIFF = [["easy", "Asan", 4], ["medium", "Orta", 3], ["hard", "Çətin", 2]];

export const TowerGame = ({ mode }) => {
  const { g, setG, busy, call, active } = useSession("/games/tower", (d) => (d.status === "lost" ? sfx.explode() : d.safe && sfx.gem()));
  const [bet, setBet] = useState(1);
  const [diff, setDiff] = useState("medium");
  const cols = g ? g.cols : DIFF.find((d) => d[0] === diff)[2];
  const rows = [...Array(8).keys()].reverse();
  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-6" data-testid="tower-game">
      <Panel className="order-2 lg:order-1">
        <div><Label>Mərc</Label><BetControl value={bet} onChange={setBet} disabled={active || busy} testid="tower-bet" /></div>
        <div><Label>Çətinlik</Label>
          <div className="grid grid-cols-3 gap-2">
            {DIFF.map(([k, l]) => (
              <button key={k} disabled={active || busy} onClick={() => { setDiff(k); setG(null); }} className={`rounded-xl py-2.5 text-sm font-bold transition-colors ${(g && active ? g.difficulty : diff) === k ? "bg-[#D4AF37] text-black" : "bg-white/5 hover:bg-white/10"}`} data-testid={`tower-diff-${k}`}>{l}</button>
            ))}
          </div>
        </div>
        <ModeNote g={g} mode={mode} />
        {active && <div className="grid grid-cols-2 gap-2"><Stat label="Əmsal" value={`x${g.multiplier}`} testid="tower-mult" /><Stat label="Mərtəbə" value={`${g.level}/8`} /></div>}
        {active ? (
          <button disabled={busy || !g.level} onClick={() => call("cashout", { game_id: g.game_id })} className="btn-gold w-full rounded-full py-4 font-display font-bold" data-testid="tower-cashout-btn">Götür {fmtAZN(g.bet * g.multiplier)}</button>
        ) : (
          <button disabled={busy} onClick={() => call("start", { bet, mode, difficulty: diff })} className="btn-gold w-full rounded-full py-4 font-display font-bold" data-testid="tower-start-btn">Başla</button>
        )}
        {g && !active && <p className={`text-center font-display font-bold ${g.status === "won" ? "text-emerald-300" : "text-red-300"}`} data-testid="tower-status">{g.status === "won" ? `Qazandınız ${fmtAZN(g.win)}` : "Tələyə düşdünüz"}</p>}
      </Panel>
      <div className="order-1 lg:order-2 rounded-3xl p-4 sm:p-6 border border-[#D4AF37]/30 space-y-2" style={{ background: "linear-gradient(180deg,#0b1d2e 0%,#06121c 60%,#0a0a0a 100%)" }} data-testid="tower-board">
        {rows.map((r) => {
          const cur = active && g.level === r;
          const picked = g?.path[r];
          const bad = g?.bad?.[r] || [];
          return (
            <div key={r} className={`flex items-center gap-2 sm:gap-3 rounded-xl p-1.5 transition-colors ${cur ? "bg-[#D4AF37]/15 ring-1 ring-[#D4AF37]" : ""}`}>
              <span className="w-14 sm:w-16 text-right font-display text-xs sm:text-sm text-[#FFD700]">x{g?.mults?.[r] ?? (0.97 * (cols / (cols - 1)) ** (r + 1)).toFixed(2)}</span>
              <div className="flex-1 grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                {[...Array(cols).keys()].map((c) => {
                  const isPick = picked === c, isBad = bad.includes(c);
                  const shown = isPick || (g && !active && g.bad);
                  return (
                    <motion.button key={c} whileHover={cur ? { scale: 1.04 } : {}} disabled={!cur || busy} onClick={() => call("pick", { game_id: g.game_id, col: c })} data-testid={`tower-cell-${r}-${c}`}
                      className={`h-10 sm:h-12 rounded-lg flex items-center justify-center transition-colors ${shown ? (isBad ? "bg-red-600/80" : "bg-emerald-500/80") : cur ? "bg-white/15 hover:bg-[#D4AF37]/40 cursor-pointer" : "bg-white/5"} ${isPick ? "ring-2 ring-white" : ""}`}>
                      {shown && (isBad ? <Skull size={18} /> : <Gem size={18} />)}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
